import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { AuthHttpInterceptor, AuthService, provideAuth0 } from '@auth0/auth0-angular';
import { Angulartics2Module } from 'angulartics2';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { UserService, WindowService } from './shared/service';
import { metaReducers, reducers } from './shared/state/root.reducer';
import { UserEffects } from './shared/state/user.effects';

const enableStoreFreeze = !environment.production;

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),

    provideAuth0({
      clientId: environment.auth.clientId,
      domain: environment.auth.domain,
      authorizationParams: {
        audience: environment.auth.audience,
        redirect_uri: `${window.location.origin}/auth-callback`,
        scope: 'openid email profile read:reports',
      },
      // Refresh token rather than a hidden iframe, which needs a third-party
      // cookie. Fallback keeps the iframe for tenants that will not issue one.
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [`${environment.apiBaseUrl}/*`],
      },
    }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },

    // Constructing AuthService is what starts the code exchange on a callback
    // URL, and it navigates to appState.target when that finishes. Do it here
    // so the login flow does not depend on which component injects it first.
    provideAppInitializer(() => {
      inject(AuthService);
    }),

    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: enableStoreFreeze,
        strictActionImmutability: true,
        strictStateSerializability: true,
        // TODO https://github.com/powerpro/horizon/issues/481
        strictActionSerializability: false,
      },
    }),
    provideEffects(UserEffects),
    // Instrumentation has to come after the store.
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
    }),

    // Neither of these ships a standalone provider function.
    importProvidersFrom(
      Angulartics2Module.forRoot({
        gst: {
          // Empty locally, which turns tracking off.
          trackingIds: environment.metrics.google.measurementId
            ? [environment.metrics.google.measurementId]
            : [],
        },
      }),
    ),

    UserService,
    WindowService,
  ],
};
